import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Group, ArrowRight, LogOut, Plus, MoreHoriz, Trash, ArrowLeft } from "iconoir-react";
import {
    fetchMyHouseholds,
    createHousehold,
    joinHousehold,
    saveActiveHousehold,
    logoutApi,
    deleteHousehold,
    leaveHousehold,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export default function HouseholdPicker() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const presetCode = (params.get("code") || "").toUpperCase().trim();
    const { user, setUser } = useAuth();
    const { t } = useT();

    const [households, setHouseholds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState(presetCode ? "join" : null); // null | create | join
    const [householdName, setHouseholdName] = useState("");
    const [code, setCode] = useState(presetCode);
    const [submitting, setSubmitting] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const list = await fetchMyHouseholds();
            setHouseholds(list);
            // if user has none and no preset, default to create
            if (!presetCode && list.length === 0) setMode("create");
            // auto-join preset code if not already member
            if (presetCode) {
                const already = list.find((x) => x.household.code === presetCode);
                if (already) {
                    enterHousehold(already);
                    return;
                }
            }
        } catch (e) {
            toast.error("Couldn't load your households");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enterHousehold = (entry) => {
        saveActiveHousehold({
            household_id: entry.household.id,
            household_name: entry.household.name,
            household_code: entry.household.code,
            member_id: entry.member.id,
            member_name: entry.member.name,
        });
        nav("/app");
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            let res;
            if (mode === "create") {
                if (!householdName.trim()) {
                    toast.error("Name your household");
                    setSubmitting(false);
                    return;
                }
                res = await createHousehold(householdName.trim());
            } else {
                if (!code.trim()) {
                    toast.error("Enter invite code");
                    setSubmitting(false);
                    return;
                }
                res = await joinHousehold(code.trim().toUpperCase());
            }
            enterHousehold({ household: res.household, member: res.member });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong");
            setSubmitting(false);
        }
    };

    const logout = async () => {
        await logoutApi();
        setUser(null);
        nav("/login");
    };

    const handleDelete = async (entry) => {
        const name = entry.household.name;
        const confirmText = window.prompt(t("picker.delete_confirm", { name }));
        if (!confirmText || confirmText.trim() !== name) {
            if (confirmText !== null) toast.error(t("picker.delete_name_mismatch"));
            return;
        }
        setBusyId(entry.household.id);
        setOpenMenuId(null);
        try {
            await deleteHousehold(entry.household.id);
            toast.success(t("picker.deleted", { name }));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("err.generic"));
        } finally {
            setBusyId(null);
        }
    };

    const handleLeave = async (entry) => {
        if (!window.confirm(t("picker.leave_confirm", { name: entry.household.name }))) return;
        setBusyId(entry.household.id);
        setOpenMenuId(null);
        try {
            await leaveHousehold(entry.household.id);
            toast.success(t("picker.left", { name: entry.household.name }));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("err.generic"));
        } finally {
            setBusyId(null);
        }
    };

    const isCreator = (entry) => entry.household.created_by === user?.user_id;

    return (
        <div className="min-h-screen w-full flex items-center justify-center p-4 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -left-16 w-64 h-64 bg-[#F3D469] rounded-full opacity-60 blur-2xl" />
            <div className="absolute -bottom-16 -right-16 w-72 h-72 bg-[#CFE4D0] rounded-full opacity-50 blur-2xl" />

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-lg"
            >
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white brut-border brut-shadow-sm rounded-xl p-1.5">
                            <img
                                src="/milo-logo.png"
                                alt="Milo"
                                className="h-9 w-auto object-contain"
                            />
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#255636]">
                                Hi, {user?.name?.split(" ")[0] || "friend"}
                            </div>
                            <div className="font-heading text-xl font-black tracking-tight">
                                Pick a household
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={logout}
                        className="w-10 h-10 brut-border rounded-lg bg-white flex items-center justify-center hover:bg-[#FBF6EB]"
                        aria-label="logout"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>

                {loading ? (
                    <div className="brut-card p-8 text-center text-sm text-[#255636]">
                        Loading your households…
                    </div>
                ) : (
                    <>
                        {households.length > 0 && (
                            <div className="space-y-2 mb-5" data-testid="household-list">
                                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#255636] px-1 mb-2">
                                    Your households
                                </div>
                                {households.map((entry) => {
                                    const creator = isCreator(entry);
                                    const menuOpen = openMenuId === entry.household.id;
                                    const busy = busyId === entry.household.id;
                                    return (
                                        <div
                                            key={entry.household.id}
                                            className="brut-card p-4 w-full flex items-center gap-3 text-left relative"
                                        >
                                            <button
                                                data-testid={`enter-household-${entry.household.id}`}
                                                onClick={() => enterHousehold(entry)}
                                                disabled={busy}
                                                className="flex items-center gap-3 flex-1 min-w-0 text-left hover:translate-y-[-1px] transition-transform disabled:opacity-50"
                                            >
                                                <div
                                                    className="w-12 h-12 rounded-xl brut-border flex items-center justify-center font-black text-lg shrink-0"
                                                    style={{ background: entry.member.color }}
                                                >
                                                    {entry.household.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold truncate">
                                                        {entry.household.name}
                                                    </div>
                                                    <div className="text-xs text-[#255636] font-medium">
                                                        code {entry.household.code}
                                                        {creator && (
                                                            <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#fbeecb] text-[#7a5c1a] rounded text-[10px] font-bold uppercase tracking-widest">
                                                                {t("picker.creator_badge")}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <ArrowRight className="w-5 h-5 shrink-0" />
                                            </button>
                                            <button
                                                data-testid={`household-menu-${entry.household.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuId(menuOpen ? null : entry.household.id);
                                                }}
                                                disabled={busy}
                                                className="w-8 h-8 brut-border rounded-lg bg-white flex items-center justify-center hover:bg-[#FBF6EB] shrink-0"
                                                aria-label="household actions"
                                            >
                                                <MoreHoriz className="w-4 h-4" />
                                            </button>
                                            {menuOpen && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 z-10"
                                                        onClick={() => setOpenMenuId(null)}
                                                    />
                                                    <div className="absolute right-2 top-14 z-20 brut-card p-1 bg-white min-w-[180px] brut-shadow">
                                                        {creator ? (
                                                            <button
                                                                data-testid={`delete-household-${entry.household.id}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(entry);
                                                                }}
                                                                className="w-full text-left px-3 py-2 text-sm font-semibold text-[#a14444] hover:bg-[#fbeaea] rounded flex items-center gap-2"
                                                            >
                                                                <Trash className="w-4 h-4" />
                                                                {t("picker.delete_household")}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                data-testid={`leave-household-${entry.household.id}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleLeave(entry);
                                                                }}
                                                                className="w-full text-left px-3 py-2 text-sm font-semibold text-[#a14444] hover:bg-[#fbeaea] rounded flex items-center gap-2"
                                                            >
                                                                <ArrowLeft className="w-4 h-4" />
                                                                {t("picker.leave_household")}
                                                            </button>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                                <div className="text-center mt-3 text-xs text-[#255636] font-medium">
                                    — or —
                                </div>
                            </div>
                        )}

                        <div className="brut-card p-6">
                            <div className="flex gap-2 mb-5">
                                <button
                                    data-testid="mode-create-btn"
                                    onClick={() => setMode("create")}
                                    className={`flex-1 py-2 px-3 rounded-lg font-bold brut-border text-sm transition-all ${
                                        mode === "create"
                                            ? "bg-[#F3D469] brut-shadow-sm"
                                            : "bg-white hover:bg-[#FBF6EB]"
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <Plus className="w-4 h-4" /> New household
                                    </div>
                                </button>
                                <button
                                    data-testid="mode-join-btn"
                                    onClick={() => setMode("join")}
                                    className={`flex-1 py-2 px-3 rounded-lg font-bold brut-border text-sm transition-all ${
                                        mode === "join"
                                            ? "bg-[#CFE4D0] brut-shadow-sm"
                                            : "bg-white hover:bg-[#FBF6EB]"
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <Group className="w-4 h-4" /> Join with code
                                    </div>
                                </button>
                            </div>

                            {mode === "create" && (
                                <form onSubmit={submit} className="space-y-3">
                                    <label className="text-xs uppercase tracking-widest font-bold block">
                                        Household name
                                    </label>
                                    <input
                                        data-testid="household-name-input"
                                        className="brut-input"
                                        placeholder="The Chen Family"
                                        value={householdName}
                                        onChange={(e) => setHouseholdName(e.target.value)}
                                    />
                                    <button
                                        data-testid="submit-create-btn"
                                        type="submit"
                                        disabled={submitting}
                                        className="brut-btn w-full justify-center"
                                    >
                                        <Home className="w-4 h-4" />{" "}
                                        {submitting ? "Creating…" : "Create household"}
                                    </button>
                                </form>
                            )}

                            {mode === "join" && (
                                <form onSubmit={submit} className="space-y-3">
                                    <label className="text-xs uppercase tracking-widest font-bold block">
                                        Invite code
                                    </label>
                                    <input
                                        data-testid="invite-code-input"
                                        className="brut-input uppercase tracking-widest font-black"
                                        placeholder="6-character code"
                                        value={code}
                                        maxLength={6}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    />
                                    <button
                                        data-testid="submit-join-btn"
                                        type="submit"
                                        disabled={submitting}
                                        className="brut-btn w-full justify-center"
                                    >
                                        <Group className="w-4 h-4" />{" "}
                                        {submitting ? "Joining…" : "Join household"}
                                    </button>
                                </form>
                            )}

                            {!mode && households.length > 0 && (
                                <div className="text-center text-sm text-[#255636] py-2">
                                    Create another or join one with a code.
                                </div>
                            )}
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
